import type { DeepPartial } from 'utility-types';

import type { FieldsMap } from '@swf/components/StarsWizard/Machine';
import type { JumpDirection } from '@swf/components/StarsWizard/components/Navigation/Navigation';
import type { Brand, Currency, FeatureFlag, FormMethod, Site } from '@swf/core/enums';
import type { License } from '@swf/core/types/Account';
import type { Error, ErrorCollection, FieldErrors } from '@swf/core/types/Response';

import type { ChallengeWindowSize } from '../utils/browserExtraInfo';

import type { CardType } from './cardType';
import type { ResponsibleGamingLimitsPayload } from './responsibleGamingLimits';
import type { CashoutStatus, DepositStatus, RealMoneyStatus, TransactionStatus } from './status';

export interface Field {
  editable?: boolean;
  name: string;
  errors?: FieldErrors;
  value?: Nullable<string | number>;
}

export type Fields = Field[];

export interface SecurityQuestion {
  id: number;
  question: string;
}

export type SecurityQuestions = SecurityQuestion[];

export interface RealMoneyInfo {
  license: License;
  site: Site;
  brand: Brand;
  realMoneyStatus: RealMoneyStatus;
  userInfoFields: Fields;
  errors: ErrorCollection;
  securityQuestions: SecurityQuestions;
  licenseMobileVerified?: boolean;
  showDepositLimits?: boolean;
}

export interface PaymentMethod {
  cardType: CardType;
  i18nSystemName?: string;
}

export type PaymentMethods = PaymentMethod[];

export interface PaySystem extends PaymentMethod {
  systemName: string;
  once: number;
  transaction: number;
  month: number;
  week: number;
  day: number;
  fee: string;
  comment: string;
  maxDeposits: number;
  depositHours: number;
  limitInfo: string;
  supportFastDeposit: boolean;
  supportCardIO: boolean;
}

export type PaySystems = PaySystem[];

enum PaySystemLimitData {
  Day = 'day',
  Month = 'month',
  Once = 'once',
  Transaction = 'transaction',
  Week = 'week'
}

export enum CashierFlowType {
  FirstDeposit = 'firstDeposit',
  FirstManageCurrencies = 'firstManageCurrencies',
  FirstP2pTransfer = 'firstP2pTransfer',
  FirstTransactionHistory = 'firstTransactionHistory',
  FirstWithdraw = 'firstWithdraw',
  WC2Deposit = 'wc2Deposit',
  WC2ManageCurrencies = 'wc2ManageCurrencies',
  WC2P2pTransfer = 'wc2P2pTransfer',
  WC2TransactionHistory = 'wc2TransactionHistory',
  WC2Withdraw = 'wc2Withdraw'
}

export enum GooglePayNativePaymentError {
  Failed = 'PAYMENT_FAILURE',
  Cancelled = 'PAYMENT_CANCELED',
  Timeout = 'PAYMENT_TIMEOUT'
}

export type MaxLimitData = Pick<
  PaySystem,
  | PaySystemLimitData.Day
  | PaySystemLimitData.Month
  | PaySystemLimitData.Transaction
  | PaySystemLimitData.Week
>;

export type MinLimitData = Pick<
  PaySystem,
  PaySystemLimitData.Once | PaySystemLimitData.Transaction
>;

export interface BonusInfo {
  bonusId: number;
  title: string;
  url: string;
  description: string;
  paySystems: CardType[];
  currencySpecifics: {
    [key in Currency]?: {
      minAmount: number;
      maxAmount: number;
    };
  };
}

export interface TicketInfo {
  ticketName: string;
  numOfTickets: number;
}

export interface PreCashinInfo {
  ticketSet: TicketInfo[];
  potentialBonusAmount: number;
  fppToExercise: number;
  maxPossibleCashInAmount: number;
  bonusId: number;
  bonusPercent: number;
  bonusMax: number;
  bonusName: string;
  bonusStart: number;
  bonusEnd: number;
  bonusUrl: string;
  outstandingCashout: number;
  releasePortion: number;
  fppReleasePortion: number;
  bonusFlags: number;
  maxNumberOfDepositsLeft: number;
  lastAllowedDepositTime: number;
  bonusCurrency: string;
  maxDaysForDepositLeft: number;
  maxNumberOfDeposits: number;
  balanceAmount: number;
  showProtectedFundsWarning: boolean;
  numOfTickets: number;
  bonusTicketName: string;
  ticketReleasePortion: number;
  maxNumTickets: number;
  amountReleasePortion: number;
  instantBonus: boolean;
  instantBonusInfo: InstantBonusInfo | null;
  pepCheck?: boolean;
  thirdPartyCheck?: boolean;
}

export interface InstantBonusInfo {
  instantBonusId: number;
  bonusAmountInBonusCurrency: number;
  bonusCurrency: string;
  bonusAmountInDepositCurrency: number;
  depositCurrency: string;
  cashPrice: number;
  cashPriceCurrency: string;
  productIds: number;
  pointsToClear: number;
  betsToClear: number;
  products: string[];
  splitWager?: boolean;
  gameMaxBetInAccountCurrency: number;
  urRedemptionLimit?: number;
}

export interface BankInfo {
  institution: string;
  branch: string;
  accountNumber: string;
  accountType: string;
}

export interface EcheckInfo {
  bankAttributes: BankInfo;
  ssnNumber: string;
  savedCardInfoId: number;
}

export interface EnvoyBankInfo {
  bankrefId: number;
  currency: Currency;
  name: string;
  city: string;
  address: string;
  swift: string;
  url: string;
  accountNum: string;
  iban: string;
  beneficiary: string;
  extraInfo: string;
  preferred: boolean;
}

export interface InovapayBankInfo {
  curr: Currency[];
  description: string;
  logoImageId: number;
  productId: string;
}

export interface FeeInfo {
  paysysFeeApplies: boolean;
  gracePeriod: number;
  totalGraceDeposits: number;
  leftGraceDeposits: number;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  rate_x_100K: number;
  minFee: number;
  maxFee: number;
  alternativePaysys: CardType;
  flatFee: boolean;
}

export interface QuickLink {
  linkDescr: string;
  linkType: number;
  linkUrl: string;
}

export type QuickLinks = QuickLink[];

export interface PaySystemInfo {
  accountId?: string;
  fraudnetSrcWebId?: string;
  hasAccount: boolean;
  minLoadLimit: number;
  maxLoadLimit: number;
  supportBillingAgreement?: boolean;
  supportedNetworks?: string[];
  merchantCapabilities?: string[];
  googlePayMerchantPublicKey?: string;
  googleApiVersion?: string;
  googleAllowedCardType?: string[];
  googlePKIProtocolVersion?: string;
  googleMerchantName?: string;
  googlePayZeroExponentCurrencies?: string;
  googleAllowedCardSchemes?: string[];
  googleAllowedAuthMethods?: string;
  googleAcquisitionCountry?: string;
}

export interface SuggestedDepositAmountsForCurrency {
  buttons: number[];
  currency: Currency;
  freeEntryValue: number;
  freeEntryValueSet: boolean;
  selectedIndex: number;
}

export interface SuggestedDepositAmounts {
  suggestedDepositAmountsForCurrency: SuggestedDepositAmountsForCurrency[];
}

export interface VoucherAmount {
  currency: Currency;
  amount: number;
}

export type VoucherAmounts = VoucherAmount[];

export interface VoucherInfo {
  voucherAmounts: VoucherAmounts;
}

export interface PrepaidBalanceInfo {
  balanceAmount: number;
  maxBalance: number;
  currency: Currency;
}

export interface PsPayBank {
  accountType: string;
  bankAccount: string;
  bankCode: string;
  bankCurrency: Currency;
  bankId: number;
  bankName: string;
  branch: string;
  cashoutSetup: boolean;
  country: string;
  default: boolean;
  nicAlias: string;
  nicValue: string;
}

export interface Rate {
  curr: Currency;
  rate: number;
  invRate: number;
}

export type Rates = Rate[];

export interface CashoutOptionRate {
  allowed: boolean;
  curr: Currency;
  explanation: string;
  invRate: number;
  rate: number;
  text: string;
}

export interface RatesInfo {
  curr: Currency;
  rates: Rates;
}

export enum DepositLimitHitTypes {
  PaysystemLimit = 'PaysystemLimit',
  SelfImposedLimit = 'SelfImposedLimit',
  TempAccountLimit = 'TempAccountLimit',
  StaffLimit = 'StaffLimit'
}

export enum DaysLimitHitTypes {
  Daily = 'Daily',
  Weekly = 'Weekly',
  Monthly = 'Monthly',
  NotApplicable = 'NotApplicable'
}

export interface PaySystemDetails {
  authenticationRequired: boolean;
  allowedSubsystems: InovapayBankInfo[];
  available: boolean;
  bonusCodes: string[];
  cardNumLen: number;
  dailyLimit: number;
  daysForClear: number;
  daysLimitHitType: DaysLimitHitTypes;
  depositLimitHitType: DepositLimitHitTypes;
  enableBonusCode: boolean;
  info: PaySystemInfo;
  isNeedAgeVerify: boolean;
  monthlyLimit: number;
  msg: string;
  paysysFee: FeeInfo;
  quickLinks: QuickLinks;
  rates: RatesInfo;
  remainingLimit: number;
  rmNokAllowed?: boolean;
  selfLimitAmount: number;
  showFastDeposit: boolean;
  suggestedDepositAmounts: SuggestedDepositAmounts;
  supportFastDeposit: boolean;
  type: number;
  weeklyLimit: number;
}

export interface PaySystemDetailsResponse {
  asPaybankInfo: Nullable<PsPayBank[]>;
  echeckInfo: Nullable<EcheckInfo>;
  mobilePhone: Nullable<string>;
  paySystemDetails: PaySystemDetails;
  prepaidBalanceInfo: Nullable<PrepaidBalanceInfo>;
  savedCardInfoId: Nullable<number>;
  transId: Nullable<number>;
  voucherInfo: Nullable<VoucherInfo>;
}

export interface DepositPayload {
  amount: number;
  applePayPaymentToken?: string;
  balanceCurrency: Currency;
  bankId?: number;
  bankInfo?: EnvoyBankInfo;
  bonusAcknowledged?: boolean;
  bonusCode?: string;
  cumulativeCheck?: boolean;
  currency: Currency;
  fraudnetMerSessionId?: string;
  googlePayCardInfo?: string;
  googlePayPaymentToken?: string;
  ignorePreviousDeclines: boolean;
  makeFastDeposit?: boolean;
  productId?: string;
  pepCheck?: boolean;
  rate: number;
  repeatedCashinDeclineOptions: string[];
  saveCardInfo?: boolean;
  savedCardInfoId?: number;
  thirdPartyCheck?: boolean;
  type: CardType;
  upsellId?: number;
  worldpay3DS2ReferenceId?: string;
  worldpay3DS2WindowSize?: ChallengeWindowSize;
}

export interface Balance {
  currency: Currency;
  chips: number;
  tMoney: number;
  balanceAvailable: number;
  balanceAllocated: number;
  balanceTotal: number;
  unclearedForCashout: number;
  availableForCashout: number;
  preferred: boolean;
  cashoutOnly: number;
  instantBonusAvailable: number;
  mFreeSpinsTotal: number;
  instantBonusInPlay: number;
  userRollsSurrenderAmount: number;
  permanent: boolean;
  created: boolean;
  instantBonusTotal: number;
}

export interface WithdrawPayload {
  accountNumber?: number;
  amount: number;
  balanceAmount: number;
  balanceCurrency: Currency;
  balances: Balance[];
  cumulativeCheck?: boolean;
  currency: Currency;
  pepCheck?: boolean;
  selectedBalance: Balance;
  thirdPartyCheck?: boolean;
  type: CardType;
  fields?: Record<string, string>;
}

export interface ManageCurrenciesPayload {
  sourceAvailableBalance: number;
  sourceAmount: number;
  sourceCurrency: Currency;
  targetAmount: number;
  targetCurrency: Currency;
}

export interface TransferPayload {
  amountFrom: number;
  currencyFrom: string;
  userComment: string;
  userTo: string;
}

export interface SensitiveDepositPayload extends DepositPayload {
  accountNumber: string;
  cvn?: string;
  expiryDate?: string;
}

export interface DepositContext {
  deposit: DepositPayload;
  previousDeposit?: Nullable<DepositPayload>;
}

export interface RedirectContext extends DeepPartial<DepositContext> {
  challenge3DS2MD?: string;
  cmsHeaderAndGridLayout: boolean;
  redirectStatus: DepositStatus;
  returnUrl: string;
  securityKey: string;
  theme: string;
  transactionId: string;
  tx_status?: TransactionStatus;
  plaidLinkToken?: Nullable<string>;
}

export interface Redirect {
  url: string;
  method: FormMethod;
  prms?: object;
}

export interface RedirectWithContext extends Redirect {
  context: RedirectContext;
}

export interface DepositSuggestion {
  cardType: CardType;
  description?: string;
}

export type DepositSuggestions = DepositSuggestion[];

export interface ResultInfo {
  balanceAmount: number;
  balanceCurrency: Currency;
  procAmount: number;
  procCurrency: Currency;
}

export interface PrepaidVisaDepositInfo {
  loadAmount: number;
  visaCharge: number;
  depositAmount: number;
  prepaidVisaBalance: number;
  masterCardJustIssued: boolean;
  loadSucceeded: boolean;
  depositSucceeded: boolean;
  cardNumber: string;
  expiryDate: string;
}

export interface WorldPay3DS2Info {
  url: string;
  jwt: string;
  md: string;
  transId: string;
}

export interface XPay3DS2Info {
  jwt: string;
}

export type FDMS3DS2Info = string;

export interface BankWireResultInfo {
  paymentReference: string;
  accountHolder: string;
  swift: string;
  city: string;
  bankAcct: string;
  bankName: string;
  country: string;
  additionalRef: string;
  specialId: string;
}

export interface DepositResult {
  bonusId: number;
  depositSuggestions: Nullable<DepositSuggestions>;
  info: Nullable<Error>;
  message: Error;
  paySystemDetails: PaySystemDetails;
  plaidLinkToken: Nullable<string>;
  realMoneyInformation: Nullable<RealMoneyInfo>;
  redirectStatus?: DepositStatus;
  redirectUrl: Redirect;
  repeatedCashinDeclineOptions: Nullable<string[]>;
  status: DepositStatus;
  systemFee: Nullable<number>;
  transactionId: string;
  trhash: string;
  uniqueRefId?: string;
  unusedLossLimit: Nullable<number>;
  url: string;
  resultInfo:
    | ResultInfo
    | PrepaidVisaDepositInfo
    | BankWireResultInfo
    | WorldPay3DS2Info
    | FDMS3DS2Info
    | XPay3DS2Info;
  processor: Nullable<string>;
  wc2Paycallback?: string;
  challenge3DS2MD?: string;
}

export interface PrepaidBalanceInfo {
  balanceAmount: number;
  maxBalance: number;
  currency: Currency;
}

export enum CashoutMethodType {
  OverCredit = 'overCredit',
  Rollback = 'rollback',
  UnknownOverCredit = 'unknownOverCredit'
}

export interface CashoutOption {
  allowed: boolean;
  cardType: CardType;
  explanation: string;
  ibanOptional: boolean;
  paySystemName: string;
  notVerifiedAccount: boolean;
  prepaidBalanceInfo: Nullable<PrepaidBalanceInfo>;
  rates: CashoutOptionRate[];
  ref: string;
  t: Nullable<number>;
  text: string;
}

export interface CashoutProcessTransactions {
  action?: number;
  amount?: number;
  fee?: number;
  ref?: string;
  type?: number;
}

export interface CashoutResult {
  message?: Error;
  realMoneyInformation?: RealMoneyInfo;
  ref?: string;
  requestedAmount?: number;
  status?: CashoutStatus;
  statusCode?: string;
  transactionId?: string;
  transactions?: CashoutProcessTransactions[];
}

export interface RollbackOption {
  amt: number;
  cardType: CardType;
  currency: Currency;
  notVerifiedAccount: boolean;
  paySystemName: string;
  processorAmount: number;
  rate: number;
  ref: string;
  rawDataRef?: string;
}

export type RollbackOverCreditOption = RollbackOption &
  Partial<CashoutOption> & {
    overCreditCurrency?: Currency;
    methodType?: CashoutMethodType;
  };

export interface DbtSignUpField {
  alpha: boolean;
  caption: string;
  extras: string;
  flags: number;
  freeField: boolean;
  hidden: boolean;
  iban: boolean;
  id: string;
  maxLength: number;
  minLength: number;
  numeric: boolean;
  optional: boolean;
  readOnly: boolean;
  userParam: boolean;
  value: string;
  verify: boolean;
}

export interface DbtSignUpFields {
  currencies: string[];
  fields: DbtSignUpField[];
}

export interface TransactionHistoryOption {
  action: string;
  amount: number;
  balanceAmount: number;
  balanceCurrency: Currency;
  cardType: CardType;
  currency: Currency;
  date: string;
  deposit: boolean;
  descriptor: string;
  fee: number;
  paysystemNameI18n: string;
  ref: string;
  refTooltip: string;
  transactionType: TransactionType;
  transactionStatus: TransactionStatus;
  transId: string;
}

export interface TransactionHistoryData extends Omit<TransactionHistoryOption, 'ref'> {
  absBalanceAmount: number;
  canBeCancelled: boolean;
  formattedRef: string;
  transId: string;
}

export interface PendingCashoutOption {
  accountNumber: string;
  balanceAmount: number;
  balanceCurrency: Currency;
  canBeCancelled: boolean;
  paySystem: string;
  processorAmount: number;
  processorCurrency: Currency;
  started: number;
  state: string;
  transactionId: string;
  type: CardType;
}

export interface CancelledTransactions {
  code: number;
  description: string;
  success: boolean;
}

export interface CancelledWithdrawResult {
  status: string;
  transactions?: CancelledTransactions[];
  message?: string;
}

export interface PsPayBank {
  accountType: string;
  bankAccount: string;
  bankCode: string;
  bankCurrency: Currency;
  bankId: number;
  bankName: string;
  branch: string;
  cashoutSetup: boolean;
  country: string;
  default: boolean;
  nicAlias: string;
  nicValue: string;
}

export interface ProvinceCity {
  city: string;
  province: string;
}

export interface BirthCountry {
  countryCode: string;
  countryName: string;
}

export interface BirthPlace {
  provinceCities: ProvinceCity[];
  countries: BirthCountry[];
  forceCountryFromServer: boolean;
}

export interface SecurityQuestion {
  id: number;
  question: string;
}

export interface RealMoneyInformation {
  addressLookupAvailable?: boolean;
  license?: License;
  site?: Site;
  brand?: Brand;
  realMoneyStatus?: RealMoneyStatus;
  userInfoFields?: Field[];
  errors?: ErrorCollection;
  securityQuestions?: SecurityQuestion[];
  licenseMobileVerified?: boolean;
  showDepositLimits?: boolean;
}

export interface FirstWizardData {
  asPaybankInfo: PsPayBank[];
  cmsHeaderAndGridLayout?: boolean;
  balances: Balance[];
  italyBirthPlace: BirthPlace;
  previousSubmittedCurrency: Currency | undefined;
  bonusCodeDescription?: string;
  bonusReadySystems: CardType[];
  cancelledWithdrawResult: CancelledWithdrawResult;
  cashierFlow: CashierFlowType;
  cashoutOptions: CashoutOption[];
  convertRate: number;
  crmBonusCode: string;
  dbtSignUpFields: DbtSignUpFields;
  deposit: DepositPayload;
  depositResult: Nullable<DepositResult>;
  envoyBanks: EnvoyBankInfo[];
  enabledBalances?: Balance[];
  enabledCurrencies?: Currency[];
  features: { [key in FeatureFlag]?: boolean };
  fieldsMap: FieldsMap[];
  goJump?: { target: string; direction: JumpDirection };
  googlePayNativeResultError?: GooglePayNativeResultError;
  hasBalanceDiscrepancy: boolean;
  initialRatesList: Rates;
  initialSourceCurrency: Currency;
  initialTargetCurrency: Currency;
  overCredit: number;
  paySystemDetails: PaySystemDetails;
  paySystems: PaySystems;
  preCashinInfo?: PreCashinInfo;
  previousDeposit: Nullable<DepositPayload>;
  realMoneyInformation: Nullable<RealMoneyInformation>;
  regularMethodSelected?: boolean;
  rmfPassed: boolean;
  depositRmfRequired: boolean;
  rollback: number;
  rollbackOptions: RollbackOption[];
  savedCardInfoId?: number;
  savedCards: SavedCards;
  selectedBalance: Balance;
  selectedOverCreditRecord?: RollbackOverCreditOption;
  signupPassed: boolean;
  transactionsData: TransactionHistoryData[];
  nPagesData: number;
  transferOutLimits: Record<string, PaySystem>;
  voucherInfo: Nullable<VoucherInfo>;
  withdrawResult: CashoutResult;
  bonusStatus: BonusStatus;
  rmfTargetToJump?: string;
  preCashinData?: {
    cumulativeCheck?: boolean;
    pepCheck?: boolean;
    thirdPartyCheck?: boolean;
  };
}

export interface FirstWizardValues {
  bonusCode?: string;
  city?: string;
  country?: string;
  deposit: DepositPayload;
  depositLimit?: ResponsibleGamingLimitsPayload;
  dob?: Date;
  firstName?: string;
  gender?: string;
  lastName?: string;
  lossLimit?: ResponsibleGamingLimitsPayload;
  manageCurrencies: ManageCurrenciesPayload;
  state?: string;
  streetAddress?: string;
  transfer: TransferPayload;
  userId?: string;
  withdraw: WithdrawPayload;
  zip?: string;
}

export interface SavedCard {
  savedCard: {
    amount: number;
    cardInfoId: number;
    currencyValid: boolean;
    expMonth: number;
    expYear: number;
    fastDeposit: boolean;
    hostileBin: boolean;
    internalPaysystem: CardType;
    lastUse: number;
    maskedCardNum: string;
    paySystem: CardType;
    procCurrency: string;
    removable: boolean;
    validationRequired: boolean;
    verified: boolean;
  };
  i18nSystemName: string;
  once: number;
  transaction: number;
}

export type SavedCards = SavedCard[];

export interface StarsWebGooglePay {
  supported: boolean;
  availableForPayment: boolean;
  // availabilityRequest is a JSON object (or JSON string) that contains a JSON description of an IsReadyToPayRequest
  // (see https://developers.google.com/android/reference/com/google/android/gms/wallet/IsReadyToPayRequest)
  checkAvailability(availabilityRequest: object): Promise<boolean>;
  // paymentData is a JSON object (or JSON string) that contains a JSON description of a PaymentDataRequest
  // (see https://developers.google.com/android/reference/com/google/android/gms/wallet/PaymentDataRequest).
  requestPayment(paymentData: object): void;
}

export interface GooglePayApiVersion {
  version: number;
  minor: number;
}

export interface GooglePayServerSettings {
  merchantPublicKey?: string;
  googlePayApiVersion: GooglePayApiVersion;
  environment?: string;
  allowedCardSchemes: string[];
  allowedAuthMethods: string;
  allowedCardType: string[];
  PKIProtocolVersion?: string;
  allowedPaymentMethodType: string;
  zeroExponentCurrencies?: string;
  merchantName?: string;
  merchantId?: string;
  countryCode?: string;
}

export interface GooglePayNativeResultError {
  status: DepositStatus;
}

export enum TransactionType {
  UNKNOWN = 'UNKNOWN',
  PURCHASE = 'PURCHASE',
  WITHDRAWAL = 'WITHDRAWAL',
  CREDIT_BACK = 'CREDIT_BACK',
  REFUND = 'REFUND',
  T_TICKET_PURCHASE = 'T_TICKET_PURCHASE',
  T_TICKET_SALE = 'T_TICKET_SALE', // sale of TSALE
  T_DEAL = 'T_DEAL', // TOURNDEAL
  RELEASED = 'RELEASED', // CASHTRANS_FLAG_RMOK_REMOVED_HOLD
  HELD = 'HELD', // DEBIT_FUNDS_RMOK_REMOVED_NAME
  CREDIT = 'CREDIT',
  DEBIT = 'DEBIT',
  COMPENSATION = 'COMPENSATION', // admin or credit with err Info "EC_COMP"
  TRANSFER_IN = 'TRANSFER_IN', // PAYSYSTEM_AG_NAME //PYR-52150, PYR-61845
  TRANSFER_OUT = 'TRANSFER_OUT', // PAYSYSTEM_AG_NAME //PYR-52150, PYR-61845
  TRANSFER_OUT_CANCELLED = 'TRANSFER_OUT_CANCELLED', // CHQ_CANCEL Asia
  WITHDRAWAL_CANCELLED = 'WITHDRAWAL_CANCELLED', // CHQ_CANCEL others
  REWARD = 'REWARD', // bonus name in eLicenceBelgium
  REBATE = 'REBATE', // bonus name in LicenseRules::wordRebateRequired(trans.licenseId)) //PYR-30972
  BONUS_CLEARED = 'BONUS_CLEARED', // general bonus
  CORRECTION = 'CORRECTION', // by admin
  WITHDRAWAL_REFUND = 'WITHDRAWAL_REFUND', // admin or non-admin
  RETURN_CORRECTION = 'RETURN_CORRECTION', // admin
  ADMIN_CREDIT = 'ADMIN_CREDIT', // applied by admin with note
  ADMIN_DEBIT = 'ADMIN_DEBIT', // applied by admin with note
  PAYMENT = 'PAYMENT', // AFFILIATE_NAME
  CHARGE_BACK = 'CHARGE_BACK', // card=CHARGEBACK_NAME
  RETURN = 'RETURN', // returns for cards not CHARGEBACK_NAME
  REVERSED_RETURN = 'REVERSED_RETURN', // card= ELV REPAYMENT
  PAYSYSTEM_FEES = 'PAYSYSTEM_FEES', // card= PAYSYSTEM_FEE_NAME
  TAX = 'TAX', // card=PAYSYSTEM_TAX_NAME !isPurchase
  MIGRATION_FUNDS_IN = 'MIGRATION_FUNDS_IN', // es migration
  MIGRATION_FUNDS_OUT = 'MIGRATION_FUNDS_OUT', // es migration
  CURRENCY_CONVERSION = 'CURRENCY_CONVERSION',
  P2P_TRANSFER_OUT = 'P2P_TRANSFER_OUT',
  P2P_TRANSFER_IN = 'P2P_TRANSFER_IN'
}

export interface DepositLimitData {
  enforcedUntil: string | null;
  limit: number;
  limitMax: number;
  limitMin: number;
  limitThreshold: number;
  regulatoryLimit: number;
}

export interface DepositLimitResponse {
  daily: DepositLimitData;
  weekly: DepositLimitData;
  monthly: DepositLimitData;
  selfLimitCurrency: string;
  rateToUsd: number;
}

interface AvailableBonusDetails {
  url: string;
  name: string;
  description: string;
}

interface TaxInfo {
  taxAmount: number;
  taxableCashout: number;
  totalBalance: number;
  unusedBalance: number;
  indiaTaxFreeCashout: number;
  indiaTaxRate_x1M: number;
}

interface PanInfo {
  panStored: boolean;
  panVerified: boolean;
}

interface TempAccountCashoutLimitAmount {
  amt: number;
  currency: string;
}

export interface BonusStatus {
  availBonusDetails: Nullable<AvailableBonusDetails[]>;
}

export interface CashoutInfoResponse {
  bonusStatus: BonusStatus;
  realMoneyInformation: string;
  authVerified: boolean;
  accValidation: { status: number };
  hasCashoutHistory: boolean;
  info: {
    pan?: PanInfo;
    tax?: TaxInfo;
    docUpdateRequired?: boolean;
  };
  operatorRate?: {
    curr?: string;
    rate?: number;
    invRate?: number;
  };
  tempAccountCashoutLimitStatus: {
    cashoutAvailable: TempAccountCashoutLimitAmount;
    cashoutLimit: TempAccountCashoutLimitAmount;
  };
}
